import type { RawChunk, ProcessResult, StreamableContent } from './chunk-processor.types';
import { detectIncompleteBlocks } from './incomplete-block-detector';

import { parseResponseContent } from '../../../parse-response-content';

export class ChunkProcessor {
  private nextExpectedChunk = 1;
  private bufferedChunks = new Map<number, RawChunk>();
  private lastProcessedChunk = 0;
  private accumulatedContent = '';

  /**
   * Process an incoming chunk from the subscription.
   * Handles ordering, buffering, and content parsing.
   */
  addChunk(chunk: RawChunk): ProcessResult {
    const { chunkNumber } = chunk;

    // Expected chunk - process immediately
    if (chunkNumber === this.nextExpectedChunk) {
      this.accumulatedContent += chunk.content;
      this.lastProcessedChunk = chunkNumber;
      this.nextExpectedChunk = chunkNumber + 1;

      // Drain any consecutive buffered chunks
      const drainedComplete = this.drainBuffer();

      if (chunk.complete || drainedComplete) {
        const content = this.buildContent(true);
        return { status: 'complete', content };
      }

      const content = this.buildContent(false);
      return { status: 'processed', content };
    }

    // Duplicate or late chunk - ignore
    if (chunkNumber < this.nextExpectedChunk) {
      return { status: 'buffered', reason: 'out_of_order' };
    }

    // Gap detected - buffer chunk for later
    this.bufferedChunks.set(chunkNumber, chunk);
    return {
      status: 'gap_detected',
      expected: this.nextExpectedChunk,
      received: chunkNumber,
    };
  }

  /**
   * Apply content from a recovery refetch.
   * Resets accumulated content and syncs chunk ordering state.
   * Returns true if any buffered chunk beyond recovery had `complete: true`.
   */
  applyRecoveryContent(content: string, lastChunkNumber: number): boolean {
    this.accumulatedContent = content;
    this.nextExpectedChunk = lastChunkNumber + 1;
    this.lastProcessedChunk = lastChunkNumber;

    // Drop buffered chunks covered by recovery
    for (const chunkNum of this.bufferedChunks.keys()) {
      if (chunkNum <= lastChunkNumber) {
        this.bufferedChunks.delete(chunkNum);
      }
    }

    // Drain any remaining consecutive chunks
    return this.drainBuffer();
  }

  /**
   * Reset all internal state for a new stream.
   */
  reset(): void {
    this.nextExpectedChunk = 1;
    this.bufferedChunks.clear();
    this.lastProcessedChunk = 0;
    this.accumulatedContent = '';
  }

  /**
   * Return final content, treating all accumulated text as complete.
   * Used when the stream ends without an explicit `complete: true` chunk.
   */
  finalize(): StreamableContent {
    return this.buildContent(true);
  }

  /**
   * Get current processor state for debugging.
   */
  getState(): {
    nextExpectedChunk: number;
    bufferedCount: number;
    lastProcessedChunk: number;
    accumulatedLength: number;
  } {
    return {
      nextExpectedChunk: this.nextExpectedChunk,
      bufferedCount: this.bufferedChunks.size,
      lastProcessedChunk: this.lastProcessedChunk,
      accumulatedLength: this.accumulatedContent.length,
    };
  }

  /**
   * Process consecutive buffered chunks starting from nextExpectedChunk.
   * Returns true if any drained chunk had `complete: true`.
   */
  private drainBuffer(): boolean {
    let foundComplete = false;

    while (this.bufferedChunks.has(this.nextExpectedChunk)) {
      const chunk = this.bufferedChunks.get(this.nextExpectedChunk)!;
      this.bufferedChunks.delete(this.nextExpectedChunk);
      this.accumulatedContent += chunk.content;
      this.lastProcessedChunk = this.nextExpectedChunk;
      this.nextExpectedChunk += 1;

      if (chunk.complete) {
        foundComplete = true;
      }
    }

    return foundComplete;
  }

  /**
   * Build streamable content from accumulated text.
   * When streaming (isFinal=false), holds back incomplete blocks.
   * When complete (isFinal=true), parses all content.
   */
  private buildContent(isFinal: boolean): StreamableContent {
    if (isFinal) {
      // Parse all content when stream is complete
      return {
        text: this.accumulatedContent,
        blocks: parseResponseContent(this.accumulatedContent),
      };
    }

    // During streaming, only parse content up to incomplete block boundary
    const { safeText } = detectIncompleteBlocks(this.accumulatedContent);

    return {
      text: safeText,
      blocks: parseResponseContent(safeText),
    };
  }
}
